import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Scroll position rules for the whole app:
 *   - Opening a new screen always starts at the top.
 *   - Going back returns you to exactly where you were.
 *
 * The app locks body scroll and scrolls `.app-content` instead, so this tracks
 * that container rather than the window. Because that container persists across
 * page changes, an unmanaged app inherits the previous screen's scroll offset -
 * which is why tapping a set from halfway down the encyclopedia used to open
 * the set already scrolled down.
 *
 * Positions are keyed by history entry (location.key), so two visits to the
 * same path keep separate positions.
 */

const STORE_KEY = 'cc_scroll_positions';
const MAX_RESTORE_TRIES = 20;
const RESTORE_INTERVAL_MS = 50;

const getScroller = () => document.querySelector('.app-content');

const readStore = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeStore = (map) => {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch {
    /* private mode or quota - scroll memory is not worth throwing over */
  }
};

export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);

  // Track the current screen's scroll position so we can come back to it.
  useEffect(() => {
    const el = getScroller();
    if (!el) return undefined;

    keyRef.current = location.key;
    let frame = null;

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const map = readStore();
        map[keyRef.current] = el.scrollTop;
        writeStore(map);
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [location.key]);

  // Apply the rules on every navigation.
  useEffect(() => {
    const el = getScroller();
    if (!el) return undefined;

    if (navigationType !== 'POP') {
      el.scrollTop = 0;
      return undefined;
    }

    const target = readStore()[location.key] || 0;
    if (!target) {
      el.scrollTop = 0;
      return undefined;
    }

    // Page content loads asynchronously, so the container is often too short to
    // accept the offset on the first attempt. Keep reapplying until it sticks.
    let tries = 0;
    let timer = null;
    const apply = () => {
      el.scrollTop = target;
      tries += 1;
      if (el.scrollTop < target && tries < MAX_RESTORE_TRIES) {
        timer = setTimeout(apply, RESTORE_INTERVAL_MS);
      }
    };
    apply();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [location.key, navigationType]);

  return null;
}
